/**
 * The inventory as the gallery reads it: works held to sell, offers not yet
 * answered, and the colour of each work's code.
 *
 * Pure. Consignments and thumbnails go in; a view comes out with the rows
 * split on `acceptedAt` and every label decided. The copy is
 * `gallery.constants.ts`; the read, the answer write and the open Sell dialog
 * are `useInventoryController.ts`. `formatConsignedDate` and `describeCodeState`
 * are borrowed by most of the other gallery models, which is why they are
 * exported from here rather than duplicated.
 *
 * A code's colour is derived, never stored, and it is derived in ONE place:
 * `codeStateOf` in `@ondo/shared`, which both the badge's words and the drawn
 * mark now read. A resale is live inventory and red at the same time, and a work
 * no appointed authority has signed is live inventory and amber — so the
 * "green-coded" count in the header is smaller than the list, correctly. The
 * artist filter narrows what is shown and never what that header counts.
 */

import {
  codeStateOf,
  countryName,
  formatMoney,
  type CodeFacts,
  type CodeState,
  type ConsignedWork,
} from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { LOCATION_LABELS } from "../lib/constants.ts";
import { formatDay } from "../lib/dates.ts";
import { GALLERY_COPY, GALLERY_MEMBERSHIP_LOST } from "./gallery.constants.ts";

/**
 * THE SCREEN HAS ONE CODE CALCULATION, AND IT IS NOT IN THIS FILE — OND-99.
 *
 * There used to be a `CodeState` here — `"green" | "red"` — and a `codeStateOf`
 * that answered it from `work.sold` and nothing else. Beside it, the drawn mark
 * ran the same ladder in `@ondo/shared`: six states, where green requires
 * no standing transfer AND a signature from an appointed authority. So one row
 * made two statements about one work, and on every work nobody with authority
 * had signed they disagreed — "Green code" beside an amber mark captioned
 * "awaiting determination". Sadie's registrar asked which was true and the
 * honest answer was "the words mean unsold and the mark means nobody has signed
 * it", which is not an answer a gallery will accept on a £2.2m picture.
 *
 * THE FIX WAS TO DELETE A COPY, NOT TO RECONCILE TWO. Both readings were correct
 * about their own question, so making them agree would have left the screen with
 * two answers that happened to match until the next fact arrived. `codeStateOf` is
 * now the only place the question is asked, and the badge renders what it said —
 * which is why `CodeState` below is re-exported rather than declared, and why
 * there is no `codeStateOf` to call.
 *
 * What the old docblock said about storage still holds and is the reason the
 * fix was available at all: the state is DERIVED, NEVER STORED.
 * `works.verification` was dropped rather than left dormant because "every
 * reader would have to remember to filter it, and the one who forgets shows a
 * sold work as held", and both facts the ladder reads —
 * `ondo.sold_work_ids()` and `ondo.authority_attested_work_ids()` — are
 * predicates over rows, computed on each read and caller-blind.
 */
export type { CodeState };

/** One row of the inventory, with every label already decided. */
export interface InventoryRow {
  consignmentId: string;
  workId: string;
  /** Opaque. 'OND-K7PQ-3M2X' — it carries no year and no initials. */
  ondoCode: string;
  title: string;
  /** 'John Currin · 2007 · Oil on canvas · 81 × 65 cm'. Absent parts are dropped. */
  identity: string;
  /**
   * 'John Currin Studio · ONDO-0451 · Consigned 04 Feb 2026 · net-of-sale'.
   *
   * One line rather than four fields, because it reads as a sentence about
   * provenance of possession and the parts are meaningless apart.
   */
  terms: string;
  /** '£1,450,000', or '' when no price was agreed — which is a real arrangement. */
  price: string;
  /**
   * Where the object is, as a sentence — OND-88 — or '' when the owner has not
   * opened it to this firm. Empty rather than "not shared": a row that said so
   * on every work would read as a complaint about owners, and the gallery's
   * question is where its stock is, not which owners declined to say.
   */
  location: string;
  artist: string;
  /**
   * The ladder's verdict, for the badge's ink and the header's count.
   *
   * `codeStateOf(codeFacts)`, computed once in `describeCodeState` — so this is the
   * same value the mark beside it is drawn from rather than a second opinion
   * about the same work. See the note at the top of the file.
   */
  code: CodeState;
  /** 'Green · Never sold'. The ladder's verdict in words — `GALLERY_COPY.codeBadge`. */
  codeLabel: string;
  /**
   * What the mark is drawn from, and what `code` above was decided by.
   *
   * ONE SET OF FACTS, ONE CALCULATION, TWO RENDERINGS. `ConsignedWork` carries
   * every fact the ladder needs — `sold`, `authorityAttested` and
   * `soldToInstitution`, each derived server-side on the read — so this screen
   * can ask the whole question. It once asked a narrower one for the badge and
   * this one for the mark, and OND-99 is what that cost.
   */
  codeFacts: CodeFacts;
  /** Null while the offer is unanswered, which is what makes it an offer. */
  acceptedAt: string | null;
  /** The work's own record. */
  href: string;
  /** The signed thumbnail, or null when there is no photograph. */
  imageUrl: string | null;
}

export type InventoryView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | { kind: "empty"; message: string; detail: string }
  | {
      kind: "ready";
      /** Works the gallery is holding to sell. The screen the design draws. */
      held: InventoryRow[];
      /**
       * Offers nobody has answered, above them.
       *
       * A separate list rather than a flag on a row, because the two are
       * different screens' worth of decision: one is stock to sell, the other is
       * a question to answer. They arrive in one request — `acceptedAt` is what
       * tells them apart — and are split exactly here.
       */
      offers: InventoryRow[];
      /** 'live inventory · 5 green-coded works'. */
      summary: string;
      /** The artists on the rows, for the filter. Empty when there is one or none. */
      artists: string[];
      artist: string;
      /** True when a filter is on and matched nothing — a different empty. */
      filteredOut: boolean;
    };

/**
 * Everything the screen renders, decided here.
 *
 * A discriminated union rather than a bag of flags, so "loading and also
 * errored" cannot be represented.
 */
export function toInventoryView(input: {
  consignments: ConsignedWork[] | null;
  error: string | null;
  images: ReadonlyMap<string, string>;
  workBase: string;
  artist: string;
}): InventoryView {
  const { consignments, error, images, workBase, artist } = input;

  if (error) return { kind: "error", title: GALLERY_COPY.errorTitle, message: error };
  if (!consignments) return { kind: "loading", message: GALLERY_COPY.loading };

  if (consignments.length === 0) {
    return {
      kind: "empty",
      message: GALLERY_COPY.empty,
      detail: GALLERY_COPY.emptyDetail,
    };
  }

  const rows = consignments.map((each) => toRow(each, workBase, images));

  // The filter narrows what is SHOWN and never what is counted. A header saying
  // "5 green-coded works" that changed when somebody picked an artist would be
  // reporting the filter rather than the inventory — `CollectionToolbar` settled
  // the same question the same way.
  const artists = distinctArtists(rows);
  const shown = artist ? rows.filter((row) => row.artist === artist) : rows;

  const held = shown.filter((row) => row.acceptedAt !== null);
  const offers = shown.filter((row) => row.acceptedAt === null);

  return {
    kind: "ready",
    held,
    offers,
    // GREEN-CODED, counted as the words say, and counted off the SAME ladder the
    // badges and the marks are drawn from. It was every held row while nothing
    // could be red; then every unsold one, which counted works whose own mark
    // was amber; now it is the rows that are actually green. A count of
    // holdings under a label saying "green-coded" would be the header reporting
    // the wrong number under the right words — twice over, as it turned out.
    summary: GALLERY_COPY.greenCoded(
      rows.filter((row) => row.acceptedAt !== null && row.code === "never-sold").length,
    ),
    // A control with one option is chrome pretending to be a control —
    // `CollectionToolbar`'s rule, and the agent's artist filter keeps it too.
    artists: artists.length > 1 ? artists : [],
    artist,
    filteredOut: shown.length === 0,
  };
}

function toRow(
  work: ConsignedWork,
  workBase: string,
  images: ReadonlyMap<string, string>,
): InventoryRow {
  return {
    consignmentId: work.consignmentId,
    workId: work.workId,
    ondoCode: work.ondoCode,
    title: work.title,
    identity: describeIdentity(work),
    terms: describeTerms(work),
    price: describePrice(work),
    location: describeLocation(work),
    artist: work.attributedName,
    ...describeCodeState({
      sold: work.sold,
      authorityAttested: work.authorityAttested,
      soldToInstitution: work.soldToInstitution,
    }),
    acceptedAt: work.acceptedAt,
    href: `${workBase}/${work.workId}`,
    imageUrl: images.get(work.workId) ?? null,
  };
}

/**
 * THE CODE'S STATE AND ITS WORDS, FROM ONE CALL — OND-99, and the whole of it.
 *
 * Every gallery row that shows a code goes through here: the inventory's held
 * rows and unanswered offers, and the sold list's placements. It asks `codeStateOf`
 * ONCE and hands back the three fields a row carries — the verdict, the words
 * for it, and the facts it was reached from — so a screen cannot acquire a
 * second answer by rendering the badge from one thing and the mark from another.
 * That is exactly what it did before, and the badge said "Green code" over an
 * amber mark for every work no appointed authority had signed.
 *
 * It takes the FACTS rather than a `ConsignedWork`, because the sold list has no
 * `ConsignedWork` to give it — a `PlacedWork` says what it is entitled to say
 * about a transfer that did not stand, and that is `sold: null` rather than
 * `false`. A helper that demanded the wire shape would have left that screen
 * writing its own ternary, which is the shape of the bug.
 */
export function describeCodeState(facts: CodeFacts): {
  code: CodeState;
  codeLabel: string;
  codeFacts: CodeFacts;
} {
  const code = codeStateOf(facts);
  return { code, codeLabel: GALLERY_COPY.codeBadge[code], codeFacts: facts };
}

/** 'John Currin · 2007 · Oil on canvas · 81 × 65 cm'. */
export function describeIdentity(work: ConsignedWork): string {
  return [work.attributedName, work.year, work.medium, work.dimensions]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
}

/**
 * Where the work came from and on what terms.
 *
 * 'John Currin Studio · ONDO-0451 · Consigned 04 Feb 2026 · net-of-sale', or
 * 'John Currin Studio · ONDO-0451 · Primary — represented artist'.
 *
 * A primary-representation arrangement prints its NAME where a consignment
 * prints its date, which is the design's own choice and the truthful one: a
 * standing arrangement with an artist has no signing date about this object.
 */
export function describeTerms(work: ConsignedWork): string {
  const parts = [work.consignorName, work.consignorOndoId];

  if (work.arrangement === "primary_representation") {
    parts.push(GALLERY_COPY.primaryRepresentation);
  } else {
    parts.push(`${GALLERY_COPY.consignedOn} ${formatConsignedDate(work.offeredAt)}`);
    const basis = GALLERY_COPY.basis[work.basis];
    if (basis) parts.push(basis);
  }

  return parts.join(" · ");
}

/**
 * Where the work is, as the owner's record says it — OND-88 — or nothing.
 *
 * 'In storage · Store B, Wiltshire · Salisbury, United Kingdom'. The typed line
 * and then the structured pair, the same two sentences `location.model.ts`
 * draws for the owner, so the registrar reads what the owner wrote and not a
 * paraphrase. Empty when the owner has not opened it, including on a ticked
 * but unanswered offer — the server sends no location for either.
 */
export function describeLocation(work: ConsignedWork): string {
  const loc = work.location;
  if (loc === null) return "";

  const parts = [LOCATION_LABELS[loc.kind]];
  if (loc.placeText) parts.push(loc.placeText);
  if (loc.countryCode) {
    const country = countryName(loc.countryCode);
    parts.push(loc.city ? `${loc.city}, ${country}` : country);
  }
  return parts.join(" · ");
}

/**
 * The asking price, or nothing at all.
 *
 * Empty rather than a dash or "on request": a consignment with no agreed number
 * simply has none, and inventing a placeholder would make an absence look like a
 * policy the gallery had chosen.
 */
export function describePrice(work: ConsignedWork): string {
  if (work.askingMinor === null || !work.currency) return "";
  return formatMoney({ minor: work.askingMinor, currency: work.currency });
}

/** '04 Feb 2026'. See `formatDay`; kept under this name for its many callers. */
export function formatConsignedDate(iso: string, locale = "en-GB"): string {
  return formatDay(iso, { locale });
}

function distinctArtists(rows: InventoryRow[]): string[] {
  return [...new Set(rows.map((row) => row.artist))].sort((a, b) => a.localeCompare(b));
}

/**
 * What went wrong, in words a gallery member can act on.
 *
 * A 403 here is not a mistyped id — this route takes no id. It means the caller
 * holds no gallery membership at all, which happens when a workspace URL
 * outlived the membership behind it.
 */
export function describeInventoryError(cause: unknown): string {
  if (cause instanceof ApiError && cause.status === 403) {
    return GALLERY_MEMBERSHIP_LOST;
  }

  return cause instanceof Error ? cause.message : GALLERY_COPY.errorTitle;
}
